import { useCallback, useRef, useState } from "react";
import type { Node, Edge } from "@xyflow/react";

const MAX_HISTORY = 20;

export interface GraphSnapshot {
  nodes: Node[];
  edges: Edge[];
}

/**
 * Snapshot-based undo/redo for the ReactFlow canvas.
 * Designed for AI-applied changes — snapshot before applying, undo to revert.
 */
export function useUndoRedo(
  nodes: Node[],
  edges: Edge[],
  setNodes: (nodes: Node[]) => void,
  setEdges: (edges: Edge[]) => void,
) {
  const undoStack = useRef<GraphSnapshot[]>([]);
  const redoStack = useRef<GraphSnapshot[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const updateFlags = useCallback(() => {
    setCanUndo(undoStack.current.length > 0);
    setCanRedo(redoStack.current.length > 0);
  }, []);

  /** Snapshot the current state before making a change. Call this before applying any AI proposal. */
  const snapshot = useCallback(() => {
    undoStack.current = [
      { nodes: [...nodes], edges: [...edges] },
      ...undoStack.current,
    ].slice(0, MAX_HISTORY);
    redoStack.current = []; // Clear redo on new action
    updateFlags();
  }, [nodes, edges, updateFlags]);

  const undo = useCallback(() => {
    const prev = undoStack.current[0];
    if (!prev) return;

    redoStack.current = [{ nodes: [...nodes], edges: [...edges] }, ...redoStack.current].slice(0, MAX_HISTORY);
    undoStack.current = undoStack.current.slice(1);

    setNodes(prev.nodes);
    setEdges(prev.edges);
    updateFlags();
  }, [nodes, edges, setNodes, setEdges, updateFlags]);

  const redo = useCallback(() => {
    const next = redoStack.current[0];
    if (!next) return;

    undoStack.current = [{ nodes: [...nodes], edges: [...edges] }, ...undoStack.current].slice(0, MAX_HISTORY);
    redoStack.current = redoStack.current.slice(1);

    setNodes(next.nodes);
    setEdges(next.edges);
    updateFlags();
  }, [nodes, edges, setNodes, setEdges, updateFlags]);

  const clear = useCallback(() => {
    undoStack.current = [];
    redoStack.current = [];
    updateFlags();
  }, [updateFlags]);

  return { snapshot, undo, redo, clear, canUndo, canRedo };
}
